import dayjs from 'dayjs';
import { REVERSE_ISO_DAYS } from 'frontend-utils/utils/constants/constant-values';
import type { RecommendationStrategy } from 'lumen/models';
import type {
  CompsetPositioningLevelValues,
  PriceRangeSettings,
} from 'lumen/models/recommendation-strategy';

type DayWithPriceRangeError = {
  dow: number;
  isConditional: boolean;
};

type MultiRoomDayWithPriceRangeError = {
  dow: number;
  roomId: string;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const isRoomValid = (minimum: number | null, maximum: number | null) => {
  return minimum === null || maximum === null || minimum <= maximum;
};

export function getDaysWithPriceRangeError(
  settings: PriceRangeSettings,
  isConditional: boolean,
): DayWithPriceRangeError[] {
  const configuration = Object.entries(settings.day_of_week ?? {});
  const daysWithPriceRangeError: DayWithPriceRangeError[] = [];

  for (const [dayOfWeek, { maximum, minimum }] of configuration) {
    const isValid = isRoomValid(minimum, maximum);

    if (!isValid) {
      daysWithPriceRangeError.push({
        dow: parseInt(dayOfWeek, 10),
        isConditional,
      });
    }
  }

  return daysWithPriceRangeError;
}

export function getMultiRoomDaysWithPriceRangeError(
  settings: PriceRangeSettings,
): MultiRoomDayWithPriceRangeError[] {
  const configuration = Object.entries(settings.overrides ?? {});
  const multiRoomDaysWithPriceRangeError: MultiRoomDayWithPriceRangeError[] =
    [];

  for (const [dayOfWeek, roomSettings] of configuration) {
    for (const [roomId, { maximum, minimum }] of Object.entries(roomSettings)) {
      const isValid = isRoomValid(minimum, maximum);

      if (!isValid) {
        multiRoomDaysWithPriceRangeError.push({
          dow: parseInt(dayOfWeek, 10),
          roomId: roomId,
        });
      }
    }
  }

  return multiRoomDaysWithPriceRangeError;
}

export function updateCompetitorImpactForSpecifiedDays(
  strategy: RecommendationStrategy,
  competitors: string[],
  newLevel: CompsetPositioningLevelValues,
  daysToUpdate: number[],
): void {
  const { settings } = strategy;

  if (
    settings.settings_type !== 'compset_positioning' ||
    !settings.day_of_week
  ) {
    return;
  }

  let newSettings = { ...settings };

  daysToUpdate.forEach((dayOfWeek) => {
    const dowSettings = newSettings.day_of_week[dayOfWeek];

    if (dowSettings) {
      competitors.forEach((competitor) => {
        newSettings = {
          ...newSettings,
          day_of_week: {
            ...newSettings.day_of_week,
            [dayOfWeek]: {
              ...newSettings.day_of_week[dayOfWeek],
              [competitor]: newLevel,
            },
          },
        };
      });
    }
  });

  strategy.settings = newSettings;
}

/**
 * Util that sorts seasonal strategies passed in, based on a few specific checks:
 * - strategies that are being created need to come first (records without an id)
 * - if strategies have an id they should be sorted ASC based on the start date (seasonStartDt)
 * - if strategies have the same start date, they'll be sorted by creation date (createdAt) - most recent first
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function sortSeasonalStrategies(strategies: RecommendationStrategy[]) {
  return strategies
    .filter((strategy) => strategy.isSeasonalStrategy)
    .sort((a, b) => {
      // if the records don't have an id yet, they're being created. In this case they should be rendered at the top
      // Note: ideally we could use isNew here, but we ran into some linting issues. Can be investigated later
      if (!a.id) {
        return -1;
      }
      if (!b.id) {
        return 1;
      }

      if (a.seasonStartDt && b.seasonStartDt && a.createdAt && b.createdAt) {
        if (a.seasonStartDt.isSame(b.seasonStartDt, 'day')) {
          return b.createdAt.isAfter(a.createdAt) ? 1 : -1;
        }

        return b.seasonStartDt.isAfter(a.seasonStartDt) ? -1 : 1;
      }
      return -1;
    });
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const getWeekDayColumns = () => {
  const startOfWeek = dayjs().startOf('week');

  return Array.from({ length: 7 }, (_, dayOfWeek) => {
    const currentDay = startOfWeek.add(dayOfWeek, 'day');
    const weekDayId = currentDay.isoWeekday();
    const isoWeekDay = REVERSE_ISO_DAYS[weekDayId];

    return {
      id: weekDayId,
      isoWeekDay,
    };
  });
};
